// ===========================================================================
// ReHome Platform — Auth Pages (Login, Register, Profile)
// Uses shared AuthContext — works with local Supabase out of the box.
// ===========================================================================

import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import {
  useAuth, useAdopterProfile, upsertAdopterProfile, ADOPTER_PROFILE_FIELDS,
  AddressAutocompleteInput, CITY_PLACE_TYPES, StaticMap,
  LIFESTYLE_QUESTIONS, TRAVEL_DISTANCE_OPTIONS, DEFAULT_MAX_TRAVEL_KM,
} from '@rehome/shared';
import { PawPrint, Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, LogOut, User as UserIcon, CheckCircle2, Sparkles, MapPin } from 'lucide-react';

// ─── "Return to" (?next=) kezelés ────────────────────────────────────────
// A védett oldalak (RequireAuth, l. App.tsx) az eredeti úticéljukat
// `?next=` paraméterben adják át. Belépés/regisztráció után oda tesszük
// vissza a felhasználót ahelyett, hogy vakon a listára dobnánk — így nem
// veszíti el, amit épp csinálni akart (pl. egy Smart Match találat
// örökbefogadási igényét).

const DEFAULT_LANDING = '/animals';

/**
 * Csak alkalmazáson BELÜLI útvonalat engedünk vissza. Egy `//evil.example`
 * vagy `https://…` érték nyílt átirányítási rést nyitna: a támadó a saját
 * oldalára küldhetné a frissen bejelentkezett felhasználót.
 */
const safeNextPath = (raw: string | null): string => {
  if (!raw) return DEFAULT_LANDING;
  if (!raw.startsWith('/') || raw.startsWith('//')) return DEFAULT_LANDING;
  // Ne küldjük vissza magára az auth oldalra — az végtelen körré válna.
  if (raw.startsWith('/login') || raw.startsWith('/register')) return DEFAULT_LANDING;
  return raw;
};

/** A `?next=` továbbfűzése a login ↔ register közti váltáshoz. */
const nextQuery = (next: string): string =>
  next === DEFAULT_LANDING ? '' : `?next=${encodeURIComponent(next)}`;

// ─── Login Page ──────────────────────────────────────────────────────────

export const LoginPage: React.FC = () => {
  const { signIn, signInWithGoogle, signOut, resetPassword, user, userProfile, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const [email, setEmail] = useState('');
  /**
   * Jelszó-visszaállítás állapota.
   *
   * A visszajelzés SZÁNDÉKOSAN nem árulja el, létezik-e a fiók ("ha a
   * megadott email regisztrálva van…") — különben a bejelentkezési oldal
   * fiók-felderítésre lenne használható.
   */
  const [resetSent, setResetSent] = useState(false);
  const [resetNeedsEmail, setResetNeedsEmail] = useState(false);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Sign out shelter users in an effect — calling signOut() during render is
  // a side effect that can trigger update loops.
  const isWrongUserType = !!(user && userProfile && userProfile.user_type !== 'platform');
  useEffect(() => {
    if (isWrongUserType) signOut();
  }, [isWrongUserType, signOut]);

  if (loading) return <LoadingScreen />;

  // Check user_type after login — shelter users cannot access platform
  if (user && userProfile) {
    if (userProfile.user_type !== 'platform') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Nincs hozzáférés</h2>
            <p className="text-gray-500">Ez a felület csak örökbefogadók számára érhető el. Menhelyi munkatársként a shelter felületen tudsz belépni.</p>
          </div>
        </div>
      );
    }
    return <Navigate to={next} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { error: err } = await signIn(email, password);
    setSubmitting(false);
    if (!err) navigate(next);
  };

  const handleForgotPassword = async () => {
    // Üres e-mail mellett nem küldünk kérést, de nem is hallgatunk: az admin
    // felület itt némán nem csinál semmit, ami könnyen tűnik elromlott
    // gombnak. Itt kimondjuk, mi hiányzik.
    if (!email.trim()) {
      setResetNeedsEmail(true);
      setResetSent(false);
      return;
    }
    setResetNeedsEmail(false);
    clearError();
    await resetPassword(email.trim());
    setResetSent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="bg-teal-600 p-3 rounded-xl inline-flex mb-4"><PawPrint className="w-8 h-8 text-white" /></div>
          <h1 className="text-2xl font-bold text-gray-900">Jelentkezz be</h1>
          <p className="text-gray-500 text-sm mt-1">a ReHome Platformra</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-6 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /><span className="flex-1">{error}</span>
            <button onClick={clearError} className="text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        {resetSent && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm mb-6">
            Ha a megadott email regisztrálva van, küldtünk egy jelszó-visszaállító linket. Ellenőrizd a postaládádat!
          </div>
        )}
        {resetNeedsEmail && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl text-sm mb-6">
            Add meg az email címedet, és újra kattints az „Elfelejtett jelszó?” gombra.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email cím</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="nev@email.hu" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jelszó</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type={showPw ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={submitting}
            className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Bejelentkezés
          </button>
        </form>

        <button
          type="button"
          onClick={handleForgotPassword}
          className="w-full text-center text-sm text-gray-400 hover:text-teal-600 mt-3 transition-colors"
        >
          Elfelejtett jelszó?
        </button>

        <div className="relative my-6"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div><div className="relative flex justify-center text-sm"><span className="bg-white px-4 text-gray-500">vagy</span></div></div>

        <button onClick={() => signInWithGoogle(next)} className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Google bejelentkezés
        </button>

        <p className="text-center text-sm text-gray-500 mt-6">Még nincs fiókod? <Link to={`/register${nextQuery(next)}`} className="text-teal-600 font-semibold hover:text-teal-800">Regisztrálj itt</Link></p>
      </div>
    </div>
  );
};

// ─── Register Page ───────────────────────────────────────────────────────

export const RegisterPage: React.FC = () => {
  const { signUp, signInWithGoogle, signOut, user, userProfile, loading, error, clearError } = useAuth();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const isWrongUserType = !!(user && userProfile && userProfile.user_type !== 'platform');
  useEffect(() => {
    if (isWrongUserType) signOut();
  }, [isWrongUserType, signOut]);

  if (loading) return <LoadingScreen />;

  // Check user_type — shelter users cannot access platform
  if (user && userProfile) {
    if (userProfile.user_type !== 'platform') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Nincs hozzáférés</h2>
            <p className="text-gray-500">Ez a felület csak örökbefogadók számára érhető el.</p>
          </div>
        </div>
      );
    }
    return <Navigate to={next} replace />;
  }

  if (user && !userProfile) {
    // Profile still loading — show spinner
    return <LoadingScreen />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirmPw) return;
    if (password.length < 8) { alert('A jelszónak legalább 8 karakter hosszúnak kell lennie.'); return; }
    setSubmitting(true);
    const { error: err } = await signUp(email, password, fullName);
    setSubmitting(false);
    if (!err) setSuccessMsg('Sikeres regisztráció! Ellenőrizd az emailedet a megerősítő linkért.');
  };

  if (successMsg) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sikeres regisztráció!</h2>
          <p className="text-gray-600">{successMsg}</p>
          <Link to={`/login${nextQuery(next)}`} className="inline-block mt-6 text-teal-600 font-semibold hover:underline">Tovább a bejelentkezéshez →</Link>
        </div>
      </div>
    );
  }

  const pwMismatch = !!(confirmPw && password !== confirmPw);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="bg-teal-600 p-3 rounded-xl inline-flex mb-4"><PawPrint className="w-8 h-8 text-white" /></div>
          <h1 className="text-2xl font-bold text-gray-900">Regisztrálj</h1>
          <p className="text-gray-500 text-sm mt-1">Csatlakozz a ReHome közösséghez</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-6 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /><span className="flex-1">{error}</span><button onClick={clearError} className="text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teljes név</label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="text" required value={fullName} onChange={e => setFullName(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Kovács Anna" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email cím</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="nev@email.hu" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jelszó</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type={showPw ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Legalább 8 karakter" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jelszó megerősítése</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type={showPw ? 'text' : 'password'} required value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none ${pwMismatch ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="••••••••" />
            </div>
            {pwMismatch && <p className="text-red-500 text-xs mt-1">A jelszavak nem egyeznek</p>}
          </div>
          <button type="submit" disabled={submitting || pwMismatch}
            className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Regisztráció
          </button>
        </form>

        <div className="relative my-6"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div><div className="relative flex justify-center text-sm"><span className="bg-white px-4 text-gray-500">vagy</span></div></div>

        <button onClick={() => signInWithGoogle(next)} className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Google regisztráció
        </button>

        <p className="text-center text-sm text-gray-500 mt-6">Már van fiókod? <Link to={`/login${nextQuery(next)}`} className="text-teal-600 font-semibold hover:text-teal-800">Jelentkezz be</Link></p>
      </div>
    </div>
  );
};

// ─── Profile Page ────────────────────────────────────────────────────────

export const ProfilePage: React.FC = () => {
  const { user, userProfile, signOut, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [showPwChange, setShowPwChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  // ── Adopter Profile State ───────────────────────────────────────────
  const { data: adopterProfile, loading: profileLoading } = useAdopterProfile();
  const [profileForm, setProfileForm] = useState<Record<string, any>>({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);

  // Populate form when adopter profile loads
  useEffect(() => {
    if (adopterProfile) {
      setProfileForm({
        housing_type: adopterProfile.housingType ?? '',
        housing_details: adopterProfile.housingDetails ?? '',
        household_members: adopterProfile.householdMembers ?? '',
        has_children: adopterProfile.hasChildren,
        children_ages: adopterProfile.childrenAges ?? '',
        has_other_pets: adopterProfile.hasOtherPets,
        other_pets_details: adopterProfile.otherPetsDetails ?? '',
        daily_routine: adopterProfile.dailyRoutine ?? '',
        work_schedule: adopterProfile.workSchedule ?? '',
        previous_pet_experience: adopterProfile.previousPetExperience,
        previous_pet_details: adopterProfile.previousPetDetails ?? '',
        has_veterinarian: adopterProfile.hasVeterinarian,
        financial_background: adopterProfile.financialBackground ?? '',
        family_plans: adopterProfile.familyPlans ?? '',
        planned_animal_function: adopterProfile.plannedAnimalFunction ?? '',
        id_card_number: adopterProfile.idCardNumber ?? '',
        // Smart Match életmód-válaszok — ugyanaz az adat, amit a matcher kérdez.
        lifestyle_housing: adopterProfile.lifestyleHousing ?? '',
        lifestyle_activity: adopterProfile.lifestyleActivity ?? '',
        lifestyle_experience: adopterProfile.lifestyleExperience ?? '',
        lifestyle_other_pets: adopterProfile.lifestyleOtherPets ?? '',
        lifestyle_kids: adopterProfile.lifestyleKids ?? '',
        home_city: adopterProfile.homeCity ?? '',
        home_lat: adopterProfile.homeLat,
        home_lng: adopterProfile.homeLng,
        home_place_id: adopterProfile.homePlaceId ?? '',
        max_travel_distance_km: adopterProfile.maxTravelDistanceKm ?? DEFAULT_MAX_TRAVEL_KM,
      });
    }
  }, [adopterProfile]);

  const handleProfileChange = (key: string, value: any) => {
    setProfileForm(prev => ({ ...prev, [key]: value }));
    setProfileSaved(false);
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    const result = await upsertAdopterProfile({
      housingType: profileForm.housing_type || null,
      housingDetails: profileForm.housing_details || null,
      householdMembers: profileForm.household_members ? Number(profileForm.household_members) : null,
      hasChildren: profileForm.has_children ?? null,
      childrenAges: profileForm.children_ages || null,
      hasOtherPets: profileForm.has_other_pets ?? null,
      otherPetsDetails: profileForm.other_pets_details || null,
      dailyRoutine: profileForm.daily_routine || null,
      workSchedule: profileForm.work_schedule || null,
      previousPetExperience: profileForm.previous_pet_experience ?? null,
      previousPetDetails: profileForm.previous_pet_details || null,
      hasVeterinarian: profileForm.has_veterinarian ?? null,
      financialBackground: profileForm.financial_background || null,
      familyPlans: profileForm.family_plans || null,
      plannedAnimalFunction: profileForm.planned_animal_function || null,
      idCardNumber: profileForm.id_card_number || null,
      lifestyleHousing: profileForm.lifestyle_housing || null,
      lifestyleActivity: profileForm.lifestyle_activity || null,
      lifestyleExperience: profileForm.lifestyle_experience || null,
      lifestyleOtherPets: profileForm.lifestyle_other_pets || null,
      lifestyleKids: profileForm.lifestyle_kids || null,
      homeCity: profileForm.home_city || null,
      // A koordináta CSAK a Google javaslatból származhat: ha a felhasználó
      // kézzel átírta a település nevét, a korábbi koordináta már máshova
      // mutatna, ezért a mező törlésekor a koordinátát is elengedjük.
      homeLat: profileForm.home_city ? (profileForm.home_lat ?? null) : null,
      homeLng: profileForm.home_city ? (profileForm.home_lng ?? null) : null,
      homePlaceId: profileForm.home_city ? (profileForm.home_place_id || null) : null,
      maxTravelDistanceKm: profileForm.max_travel_distance_km
        ? Number(profileForm.max_travel_distance_km)
        : null,
    });
    setProfileSaving(false);
    if (result) setProfileSaved(true);
  };

  if (!user) return <Navigate to="/login" replace />;

  const handleLogout = async () => { await signOut(); navigate('/login'); };

  const handlePasswordChange = async (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(false);

    if (newPassword.length < 8) {
      setPwError('A jelszónak legalább 8 karakter hosszúnak kell lennie.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('A két jelszó nem egyezik.');
      return;
    }

    setPwSubmitting(true);
    const { error } = await updatePassword(newPassword);
    setPwSubmitting(false);

    if (error) {
      setPwError(error);
    } else {
      setPwSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => { setPwSuccess(false); setShowPwChange(false); }, 2000);
    }
  };

  // Group fields by category
  const categorizedFields = ADOPTER_PROFILE_FIELDS.reduce((acc, f) => {
    if (!acc[f.category]) acc[f.category] = [];
    acc[f.category].push(f);
    return acc;
  }, {} as Record<string, Array<typeof ADOPTER_PROFILE_FIELDS[number]>>);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
      {/* Basic account info card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xl font-bold">
            {(user.user_metadata?.full_name || user.email || '?')[0].toUpperCase()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{user.user_metadata?.full_name || 'Ismeretlen'}</h2>
            <p className="text-gray-500 text-sm">{user.email}</p>
          </div>
        </div>
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex justify-between py-2 border-b border-gray-100"><span>Email</span><span className="font-medium">{user.email}</span></div>
          <div className="flex justify-between py-2 border-b border-gray-100"><span>Regisztráció dátuma</span><span className="font-medium">{new Date(user.created_at).toLocaleDateString('hu-HU')}</span></div>
          <div className="flex justify-between py-2 border-b border-gray-100"><span>Fiók típusa</span><span className="font-medium">{userProfile?.user_type === 'platform' ? 'Örökbefogadó' : 'Menhelyi'}</span></div>
        </div>

        {/* Password Change */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          {!showPwChange ? (
            <button onClick={() => setShowPwChange(true)} className="w-full py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
              <Lock className="w-4 h-4" /> Jelszó módosítása
            </button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-3">
              <h3 className="font-semibold text-gray-800">Új jelszó beállítása</h3>
              {pwError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0" />{pwError}</div>}
              {pwSuccess && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-xl text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0" />Jelszó sikeresen módosítva!</div>}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Új jelszó</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none text-sm" placeholder="Minimum 8 karakter" autoComplete="new-password" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Jelszó megerősítése</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none text-sm" placeholder="Jelszó újra" autoComplete="new-password" />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowPwChange(false)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Mégse</button>
                <button type="submit" disabled={pwSubmitting} className="flex-1 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-1">
                  {pwSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Mentés
                </button>
              </div>
            </form>
          )}
        </div>

        <button onClick={handleLogout} className="w-full mt-6 bg-red-50 text-red-700 py-3 rounded-xl font-bold hover:bg-red-100 transition-colors flex items-center justify-center gap-2">
          <LogOut className="w-4 h-4" /> Kijelentkezés
        </button>
      </div>

      {/* Adopter Profile Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Örökbefogadói Profil</h2>
            <p className="text-sm text-gray-500 mt-1">
              Ezek az adatok segítik a menhelyeket a megfelelő állat kiválasztásában. Minden mező opcionális — csak azt töltsd ki, amit szeretnél.
            </p>
          </div>
        </div>

        {/* Privacy notice */}
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-xl text-xs mb-6">
          <strong>Adatkezelési tájékoztató:</strong> A megadott adatokat a ReHome bizalmasan kezeli. Egy menhely csak akkor fér hozzá az adataidhoz, ha egy adott állatnál az örökbefogadási folyamat részeként kéri be őket. A csillaggal (*) jelölt mezők érzékeny adatok — ezeket csak akkor add meg, ha egy menhely kifejezetten kéri.
        </div>

        {!showProfileForm ? (
          <button onClick={() => setShowProfileForm(true)} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl font-medium text-gray-600 hover:border-teal-400 hover:text-teal-600 transition-colors flex items-center justify-center gap-2">
            <UserIcon className="w-4 h-4" />
            {adopterProfile ? 'Profil szerkesztése' : 'Örökbefogadói profil kitöltése'}
          </button>
        ) : (
          <div className="space-y-6">
            {profileLoading ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 text-teal-600 animate-spin" /></div>
            ) : (
              <>
                {/* ── Smart Match kérdései ────────────────────────────────
                    Ugyanaz az adat, amit a Smart Match kérdez: ha itt
                    kitöltöd, a matching nem kérdezi újra; ha ott futtatod le,
                    a válaszok ide mentődnek. Egy adat, két nézet. */}
                <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-4">
                  <div className="flex items-start gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-sm text-teal-900">Smart Match kérdései</h3>
                      <p className="text-xs text-teal-800/80 mt-0.5">
                        Ezekre a kérdésekre a Smart Match is rákérdez. Amit itt megadsz, azt ott
                        már nem kell újra kiválasztanod — és fordítva.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {LIFESTYLE_QUESTIONS.map(q => (
                      <div key={q.key}>
                        <label htmlFor={`lifestyle-${q.key}`} className="block text-sm font-medium text-gray-700 mb-1">
                          {q.profileLabel}
                        </label>
                        <select
                          id={`lifestyle-${q.key}`}
                          value={profileForm[q.column] ?? ''}
                          onChange={e => handleProfileChange(q.column, e.target.value)}
                          className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none"
                        >
                          <option value="">— Nincs megadva —</option>
                          {q.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Lakóhely és vállalt távolság ───────────────────────── */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-sm text-gray-800">Lakóhely és távolság</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        A Smart Match ez alapján hozza előrébb a hozzád közelebbi menhelyek állatait.
                        A távolabbiak <span className="font-medium">nem esnek ki</span>, csak hátrébb kerülnek.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label htmlFor="home-city" className="block text-sm font-medium text-gray-700 mb-1">
                        Település
                      </label>
                      <AddressAutocompleteInput
                        id="home-city"
                        value={profileForm.home_city ?? ''}
                        onChange={city => {
                          // Kézi átírásnál a koordináta érvényét veszti (l. mentés).
                          setProfileForm(prev => ({ ...prev, home_city: city, home_lat: null, home_lng: null, home_place_id: '' }));
                          setProfileSaved(false);
                        }}
                        onPlaceSelected={({ address, lat, lng, placeId }) => {
                          setProfileForm(prev => ({ ...prev, home_city: address, home_lat: lat, home_lng: lng, home_place_id: placeId }));
                          setProfileSaved(false);
                        }}
                        includedPrimaryTypes={CITY_PLACE_TYPES}
                        placeholder="Pl. Kecskemét"
                        className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                        hint="Csak a településed kell — pontos lakcímet nem kérünk és nem tárolunk."
                      />
                      {/* Település-szintű nagyítás: a pontos lakcímet nem
                          kérjük, tehát a térkép sem mutathat többet annál,
                          amit rögzítünk — a visszaellenőrzéshez viszont ennyi
                          elég ("tényleg ezt a Szentgyörgyöt választottam?"). */}
                      {profileForm.home_lat != null && profileForm.home_lng != null && (
                        <StaticMap
                          lat={profileForm.home_lat}
                          lng={profileForm.home_lng}
                          label={profileForm.home_city ?? null}
                          zoom={11}
                          height={150}
                          className="mt-2"
                        />
                      )}
                      {profileForm.home_city && !profileForm.home_lat && (
                        <p className="text-xs text-amber-600 mt-1.5">
                          Válaszd ki a települést a legördülő listából, hogy a távolság számolható legyen.
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="max-travel" className="block text-sm font-medium text-gray-700 mb-1">
                        Meddig mennél el az állatért?
                      </label>
                      <select
                        id="max-travel"
                        value={profileForm.max_travel_distance_km ?? DEFAULT_MAX_TRAVEL_KM}
                        onChange={e => handleProfileChange('max_travel_distance_km', Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none"
                      >
                        {TRAVEL_DISTANCE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {Object.entries(categorizedFields).map(([category, fields]) => (
                  <div key={category}>
                    <h3 className="font-semibold text-sm text-gray-500 uppercase tracking-wider mb-3">{category}</h3>
                    <div className="space-y-3">
                      {fields.map(f => {
                        const value = profileForm[f.key];
                        const isSensitive = f.sensitive;
                        return (
                          <div key={f.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {f.label}
                              {isSensitive && <span className="text-amber-600 ml-1" title="Érzékeny adat">*</span>}
                            </label>
                            {'options' in f && f.options ? (
                              <select
                                value={value ?? ''}
                                onChange={e => handleProfileChange(f.key, e.target.value || null)}
                                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                              >
                                <option value="">— Nincs megadva —</option>
                                {f.options.map((opt: string) => (
                                  <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
                                ))}
                              </select>
                            ) : f.key === 'has_children' || f.key === 'has_other_pets' || f.key === 'previous_pet_experience' || f.key === 'has_veterinarian' ? (
                              <select
                                value={value === true ? 'true' : value === false ? 'false' : ''}
                                onChange={e => {
                                  const v = e.target.value;
                                  handleProfileChange(f.key, v === '' ? null : v === 'true');
                                }}
                                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                              >
                                <option value="">— Nincs megadva —</option>
                                <option value="true">Igen</option>
                                <option value="false">Nem</option>
                              </select>
                            ) : f.key === 'household_members' ? (
                              <input
                                type="number" min="1" max="20"
                                value={value ?? ''}
                                onChange={e => handleProfileChange(f.key, e.target.value ? Number(e.target.value) : null)}
                                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                                placeholder="Pl. 4"
                              />
                            ) : (
                              <textarea
                                value={value ?? ''}
                                onChange={e => handleProfileChange(f.key, e.target.value || null)}
                                rows={2}
                                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-teal-500 outline-none resize-y"
                                placeholder={isSensitive ? '⚠️ Érzékeny adat — csak ha menhely kéri' : `Pl. ${f.label.toLowerCase()}`}
                              />
                            )}
                            {isSensitive && <p className="text-xs text-amber-600 mt-0.5">Érzékeny adat — a menhelyek csak indokolt esetben kérik be.</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {profileSaved && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-xl text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />Profil sikeresen mentve!
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowProfileForm(false)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                    Bezárás
                  </button>
                  <button onClick={handleProfileSave} disabled={profileSaving}
                    className="flex-1 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-1">
                    {profileSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Mentés
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Animal Profile (stub) ───────────────────────────────────────────────

export const AnimalProfilePage: React.FC = () => (
  <div className="text-center py-16"><h2 className="text-2xl font-bold text-gray-800 mb-4">Állat Profil</h2><p className="text-gray-500">Ez az oldal a Supabase integráció után kerül véglegesítésre.</p></div>
);

const LoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 text-teal-600 animate-spin" /></div>
);
